import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { getOrCreateLeaderboard } from '../api/leaderboard_api';
import { getAchievements } from '../game/achievements';

function formatPercent(value) {
  return `${Math.round(value * 100)}%`;
}

function normalizeLeaderboardEntry(entry, index, currentDeviceId, currentPlayerName) {
  const deviceId = entry.deviceId || entry.id || `api-player-${index + 1}`;
  const total = Number(entry.total) || 0;
  const correct = Number(entry.correct ?? entry.score) || 0;
  const winStream = Number(entry.winStream ?? entry.bestStreak ?? entry.streak) || 0;
  const accuracy = Number.isFinite(Number(entry.accuracy))
    ? Number(entry.accuracy)
    : total > 0
      ? correct / total
      : 0;

  return {
    id: deviceId,
    rank: entry.rank || index + 1,
    name: deviceId === currentDeviceId ? currentPlayerName : entry.playerName || entry.name || `Player ${index + 1}`,
    correct,
    total,
    winStream,
    accuracy,
    latestPlayedAt: entry.latestPlayedAt || entry.playedAt || '',
    isCurrentPlayer: deviceId === currentDeviceId,
    source: 'API',
  };
}

function sortLeaderboardEntries(a, b) {
  if (a.rank && b.rank) {
    return a.rank - b.rank;
  }

  if (b.winStream !== a.winStream) {
    return b.winStream - a.winStream;
  }

  if (b.accuracy !== a.accuracy) {
    return b.accuracy - a.accuracy;
  }

  if (b.correct !== a.correct) {
    return b.correct - a.correct;
  }

  return new Date(b.latestPlayedAt).getTime() - new Date(a.latestPlayedAt).getTime();
}

function Leaderboard({ entries, loading, error }) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography fontWeight={900}>Leaderboard</Typography>

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography color="text.secondary">Loading leaderboard</Typography>
            </Stack>
          )}

          {error && <Alert severity="warning">{error}</Alert>}

          {!loading && entries.length > 0 ? (
            <Stack spacing={1}>
              {entries.map((entry, index) => (
                <Box key={entry.id} className="leaderboard-row">
                  <Typography className="leaderboard-rank">{entry.rank || index + 1}</Typography>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography className="leaderboard-name" noWrap>
                        {entry.name}
                      </Typography>
                      {entry.isCurrentPlayer && <Chip label="You" size="small" color="primary" />}
                    </Stack>

                    <Typography color="text.secondary">
                      {entry.winStream} win streak - {entry.correct} correct from {entry.total} answer
                      {entry.total === 1 ? '' : 's'}
                    </Typography>

                    <Typography className="leaderboard-achievement-count">{entry.source}</Typography>
                  </Box>

                  <Typography className="leaderboard-score">{formatPercent(entry.accuracy)}</Typography>
                </Box>
              ))}
            </Stack>
          ) : !loading ? (
            <Typography color="text.secondary">Play a round to create your first leaderboard entry.</Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function getAchievementPlayerPercentages(entries) {
  const totalPlayers = entries.length;

  if (totalPlayers === 0) {
    return {};
  }

  return entries.reduce((percentages, entry) => {
    getAchievements(entry).forEach((achievement) => {
      if (achievement.unlocked) {
        percentages[achievement.id] = (percentages[achievement.id] || 0) + 1;
      }
    });

    return percentages;
  }, {});
}

function AchievementsPanel({ achievements, playerPercentages }) {
  const unlockedCount = achievements.filter((achievement) => achievement.unlocked).length;
  const sortedAchievements = [...achievements].sort((first, second) => {
    if (first.unlocked !== second.unlocked) {
      return Number(second.unlocked) - Number(first.unlocked);
    }

    if (first.unlocked) {
      return (playerPercentages[first.id] || 0) - (playerPercentages[second.id] || 0);
    }

    return second.progress - first.progress;
  });

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography fontWeight={900}>Achievements</Typography>
            <Typography color="text.secondary">
              {unlockedCount} of {achievements.length} unlocked from your current stats.
            </Typography>
          </Box>

          <Box className="achievement-grid">
            {sortedAchievements.map((achievement, index) => (
              <Box
                key={achievement.id}
                className={`achievement-tile ${achievement.unlocked ? 'is-unlocked' : 'is-locked'}`}
              >
                <Box className="achievement-badge">{achievement.unlocked ? 'DONE' : index + 1}</Box>
                <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography className="achievement-title">{achievement.title}</Typography>
                    <Typography className="achievement-player-percent">
                      {playerPercentages[achievement.id] || 0}% players
                    </Typography>
                  </Stack>
                  <Typography className="achievement-description">{achievement.description}</Typography>
                  <Stack spacing={0.35}>
                    <LinearProgress
                      variant="determinate"
                      value={achievement.progress}
                      className="achievement-progress"
                    />
                    <Typography className="achievement-progress-label">{achievement.progress}% complete</Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function HighScoresScreen({ deviceId, playerName, onNavigate }) {
  const [apiLeaderboardEntries, setApiLeaderboardEntries] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState('');

  const leaderboardEntries = [...apiLeaderboardEntries].sort(sortLeaderboardEntries);
  const currentEntry = leaderboardEntries.find((entry) => entry.id === deviceId) || {
    correct: 0,
    total: 0,
    winStream: 0,
    accuracy: 0,
  };
  const achievements = getAchievements(currentEntry);
  const achievementCounts = getAchievementPlayerPercentages(leaderboardEntries);
  const playerPercentages = Object.fromEntries(
    Object.entries(achievementCounts).map(([achievementId, count]) => [
      achievementId,
      Math.round((count / Math.max(leaderboardEntries.length, 1)) * 100),
    ])
  );

  useEffect(() => {
    let isMounted = true;

    setLeaderboardLoading(true);
    setLeaderboardError('');

    getOrCreateLeaderboard({ deviceId, playerName })
      .then((leaderboard) => {
        if (!isMounted) {
          return;
        }

        const entries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];

        setApiLeaderboardEntries(
          entries.map((entry, index) => ({
            ...normalizeLeaderboardEntry(entry, index, deviceId, playerName),
            source: leaderboard?.fallback ? 'API fallback' : 'API',
          }))
        );
      })
      .catch((error) => {
        if (isMounted) {
          setLeaderboardError(error.message);
          setApiLeaderboardEntries([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLeaderboardLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [deviceId, playerName]);

  return (
    <Box className="screen">
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Typography fontWeight={900}>Average Accuracy</Typography>
              <Typography className="accuracy-percent">{formatPercent(currentEntry.accuracy)}</Typography>
              <Typography color="text.secondary">
                Best win streak {currentEntry.winStream}. {currentEntry.correct} correct from {currentEntry.total} total
                answer{currentEntry.total === 1 ? '' : 's'}.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="overline" color="text.secondary">
            Global leaderboard
          </Typography>
          <Typography variant="h2">Leaderboard</Typography>
        </Box>

        <Leaderboard entries={leaderboardEntries} loading={leaderboardLoading} error={leaderboardError} />

        <AchievementsPanel achievements={achievements} playerPercentages={playerPercentages} />

        <Stack spacing={1.25}>
          <Button variant="contained" size="large" startIcon={<PlayArrowRoundedIcon />} onClick={() => onNavigate('/game')}>
            Keep Playing
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default HighScoresScreen;
